<template>
  <div>
    <div v-if="showDescription" class="flex flex-col mb-4">
      <p class="checklist-builder__second-stage-title">
        {{ $t('template-builder.approvals.title') }}
      </p>
      <p class="text-sm text-70">
        {{ $t('template-builder.approvals.description') }}
      </p>
    </div>

    <BigField>
      <template v-slot:field>
        <checkbox-field
          :value="approvalsEnabled"
          :label="$t('template-builder.approvals.enable.label')"
          @input="toggleApprovalsEnabled"
        />
      </template>
    </BigField>

    <div v-if="approvalsEnabled">
      <choose-user-field
      :hide-label="true"
      :options="options"
      :value="checklistApproversOptions"
      :errors="[]"
      :identifier="identifier"
      @input="setOptions(arguments[0])">
        <template v-slot:beforeList>
          <a class="choose-user-field__header-link--default has-text-primary has-text-weight-semibold" @click="selectAllUsers">
            <icon icon="check-double" />
            <span>{{ $t('common.select-all') }}</span>
          </a>
          <a class="choose-user-field__header-link--default has-text-primary has-text-weight-semibold" @click="deselectAllUsers">
            <icon icon="minus" />
            <span>{{ $t('common.deselect-all') }}</span>
          </a>
        </template>
      </choose-user-field>

      <BigField>
        <template v-slot:field>
          <checkbox-field
              :value="entityRestrictions"
              :label="$t('template-builder.approvals.entity-restrictions.label')"
              @input="toggleEntityRestrictions"
          />
        </template>
      </BigField>
    </div>
  </div>
</template>

<script>
  import _, { kebabCase } from 'lodash';
  import BigField from '@/_legacy/views/ChecklistBuilder/BigField.vue';
  import SelectControl from '@/_legacy/components/Controls/SelectControl.vue';
  import authMixin from '@/_legacy/mixins/authMixin';
  import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
  import { ChooseUserField } from '@/ui/components/user/ChooseUserField';
  import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
  import { EventBus } from '@/ui/EventBus';
  import { Events } from '@/ui/events';
  import { CheckboxField } from '@/ui/components/form';
  import { Icon } from '@/ui/components/icon/Icon';
  import { useTask } from '@/ui/concerns';
  import { filter as userFilter } from '@/services/api/resources/user';
  import { Permissions } from '@/ui/auth/auth.types';
  import { filterListToOptionsUsers } from '@/ui/helpers';

  export default {
    components: {
      CheckboxField,
      UpgradeAd,
      BigField,
      SelectControl,
      ChooseUserField,
      Icon,
    },

    mixins: [
      authMixin,
    ],

    props: {
      checklistApprovers: {
        type: Object,
        default: null,
      },
      users: {
        type: Array,
        required: true,
      },
      issue: {
        type: Boolean,
        required: false,
        default: false,
      },
      approvalsEnabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      entityRestrictions: {
        type: Boolean,
        required: false,
        default: false,
      },
      showDescription: {
        type: Boolean,
        required: false,
        default: false,
      },
      identifier: {
        type: String,
        required: false,
        default: CreateUserModelIdentifier.checklistBuilderApprovers,
      }
    },

    data() {
      return {
        approvers: {
          users: []
        },
        createUserModelIdentifier: CreateUserModelIdentifier,
        usersWithPermissions: [],
        userOptions: [],
      };
    },

    computed: {
      options() {
        const options = this.getOptions(this.userOptions);

        return _.sortBy(options, 'label');
      },

      checklistApproversOptions() {
        return this.getOptions(this.approvers.users);
      },
    },

    watch: {
      users: {
        immediate: true,
        handler(newUsers) {
          this.userOptions = newUsers || [];
        },
      },
    },

    mounted() {
      EventBus.$on(Events.userCreated, this.onUserCreated);

      this.approvers = this.checklistApprovers || {
        users: []
      };
      this.userOptions = this.users || [];
    },

    destroyed() {
      EventBus.$off(Events.userCreated, this.onUserCreated);
    },

    methods: {
      selectAllUsers() {
        const options = this.getOptions(this.users).map(option => option.value);
        this.setOptions(options);
      },

      deselectAllUsers() {
        this.setOptions([]);
      },

      toggleApprovalsEnabled() {
        this.$emit('update:approvalsEnabled', !this.approvalsEnabled);
      },

      toggleEntityRestrictions() {
        this.$emit('update:entityRestrictions', !this.entityRestrictions);
      },

      async onUserCreated({ response, identifier }) {
        if (this.identifier !== identifier) {
          return;
        }

        await this.fetchActionApprovalUsers();

        if (this.usersWithPermissions.includes(response.id)) {
          this.addNewOption(response.id);
        }
      },

      addNewOption(userId) {
        const approvers = [];

        _.each(this.approvers.users, (option, key) => {
          approvers.push(option);
        });

        approvers.push(userId);

        this.$set(this.approvers, 'users', approvers);

        if (this.issue) {
          this.$emit('update:checklistApprovers', this.approvers);
          return;
        }

        if (!this.editableByAll) {
          this.$emit('update:checklistFormApprovers', this.approvers);
        }
      },

      setOptions(options) {
        const optionSet = _.groupBy(
          _.filter(options, option => option !== 'all'),
          option => _.split(option, '_')[0],
        );
        _.each(optionSet, (option, key) => {
          option = _.map(option, o => parseInt(_.split(o, '_')[1], 10));
          this.$set(this.approvers, key, option);
        });
        !optionSet.users && this.$set(this.approvers, 'users', []);

        if (this.issue) {
          this.$emit('update:checklistApprovers', this.approvers);
          return;
        }

        if (!this.editableByAll) {
          this.$emit('update:checklistFormApprovers', this.approvers);
        }
      },

      getOptions(users) {
        return this.getOptionItems(users, 'users');
      },

      async fetchActionApprovalUsers() {
        const permissionsForActions = [
          Permissions.APPROVE_ALL_ACTIONS,
          Permissions.APPROVE_ASSIGNED_ACTIONS,
        ];
        const permissionsForInspections = [
          Permissions.APPROVE_ALL_INSPECTIONS,
          Permissions.APPROVE_ASSIGNED_INSPECTIONS,
        ];
        const permissions = this.issue
          ? permissionsForInspections
          : permissionsForActions;
        const getApprovalUsers = useTask(
          () => userFilter({
            permission: permissions.map(kebabCase)
          }),
          response => {
            this.usersWithPermissions = response.data.map(user => user.id);
            const options = [
              ...filterListToOptionsUsers(response.data),
            ];
            this.$set(this, 'userOptions', options);
          },
        );
        return getApprovalUsers.run();
      },

      getOptionItems(items, group) {
        const itemsCopy = _.cloneDeep(items);
        return itemsCopy.map(item => {
          if (_.isString(item) || _.isInteger(item)) {
            return `${group}_${item}`;
          }
          item.value = `${group}_${_.last(_.split(item.value, '_'))}`;
          return item;
        });
      },
    }
  };
</script>
